import { useState } from 'react'
import { capitalize } from 'lodash-es'
import {
  IS_RESOLVING_INCIDENTS_ALLOWED,
  INCIDENTS_CODES_ALLOWED_FOR_RESOLUTION,
  SHIPPING_STATUS_CODES,
  RESOLVED_STATUS_CODES,
} from '../common/constants'
import {
  getIsDownloadPODAllowed,
  downloadPOD,
  getIsUnscheduledPickup,
} from '../context/business'
import { canBeReturned } from '../App/ShippingsTracking/TrackingTable/useShippingActions'
import {
  CANCELLED,
  DISABLED,
  CURRENT,
  PAST,
  ERROR,
} from '../ui/modules/TrackingLine/constants'
import useTranslations from '../i18n/useTranslations'

const getIsIncidentResolutionBtnShown = (code, step) => {
  return (
    Boolean(code) &&
    step === SHIPPING_STATUS_CODES.delivering &&
    IS_RESOLVING_INCIDENTS_ALLOWED &&
    INCIDENTS_CODES_ALLOWED_FOR_RESOLUTION.includes(code)
  )
}

const filterStatusList = (statusList, passedStatusCodes) => {
  let filteredStatusList = [...statusList]

  if (!passedStatusCodes.includes(SHIPPING_STATUS_CODES.returned)) {
    filteredStatusList = filteredStatusList.filter(
      item => item.code !== SHIPPING_STATUS_CODES.returned,
    )
  } else if (passedStatusCodes.includes(SHIPPING_STATUS_CODES.inTransit)) {
    filteredStatusList = filteredStatusList.filter(
      item =>
        passedStatusCodes.includes(item.code) ||
        item.code === SHIPPING_STATUS_CODES.collected,
    )
  } else {
    filteredStatusList = filteredStatusList.filter(item =>
      passedStatusCodes.includes(item.code),
    )
  }

  if (!passedStatusCodes.includes(SHIPPING_STATUS_CODES.pudo)) {
    filteredStatusList = filteredStatusList.filter(
      item => item.code !== SHIPPING_STATUS_CODES.pudo,
    )
  }

  return filteredStatusList
}

const useTrackingLine = (
  statusList,
  onClickResolveIncident,
  fullTrackingData,
) => {
  const formatMessage = useTranslations()

  // activate returns helpers

  const [activateReturnShippingInfo, setActivateReturnShippingInfo] = useState()

  const cancelActivateReturn = () => setActivateReturnShippingInfo(false)

  // scheduled pickup helpers

  const [scheduledPickupShippingsInfo, setScheduledPickupShippingsInfo] =
    useState([])

  const cancelScheduledPickup = () => setScheduledPickupShippingsInfo([])

  // status type

  const getStatusType = (
    i,
    stepData,
    isPast,
    isCancelled,
    activeStatusIndex,
    passedStatusCodes,
    hasIncident,
    currentStatusCode,
    isUnscheduledPickup,
  ) => {
    if (isCancelled) return i === 0 ? CANCELLED : DISABLED
    if (hasIncident && i === activeStatusIndex) return ERROR
    if (
      isUnscheduledPickup &&
      (currentStatusCode === SHIPPING_STATUS_CODES.manifested ||
        !currentStatusCode) &&
      stepData.code === SHIPPING_STATUS_CODES.collected
    )
      return ERROR
    if (
      isUnscheduledPickup &&
      stepData.code === SHIPPING_STATUS_CODES.manifested
    )
      return CURRENT
    if (isPast) return PAST
    if (i === activeStatusIndex) return CURRENT
    if (passedStatusCodes.includes(stepData.code)) return PAST
    return DISABLED
  }

  const getTrackingData = shipping => {
    if (!shipping || !statusList || !fullTrackingData) {
      return undefined
    }

    const { isCancelled } = shipping.info
    const lastStatus = [...fullTrackingData]
      .reverse()
      .find(x => x.type === 'STATUS')

    const hasIncident = lastStatus ? Boolean(lastStatus.incidentCode) : false

    const adjustedFullTrackingData = fullTrackingData.map(item => ({
      ...item,
      shippingStatusCode:
        item.shippingStatusCode === SHIPPING_STATUS_CODES.incidentDelivering
          ? SHIPPING_STATUS_CODES.delivering
          : item.shippingStatusCode,
    }))

    let validPassedStatusCodes = adjustedFullTrackingData.map(
      item => item.shippingStatusCode,
    )

    const currentStatusCode =
      validPassedStatusCodes[validPassedStatusCodes.length - 1]
    const isCurrentStatusResolved =
      RESOLVED_STATUS_CODES.includes(currentStatusCode) &&
      INCIDENTS_CODES_ALLOWED_FOR_RESOLUTION.includes(
        adjustedFullTrackingData[adjustedFullTrackingData?.length - 1]
          .incidentCode,
      )

    if (RESOLVED_STATUS_CODES.includes(currentStatusCode)) {
      validPassedStatusCodes = validPassedStatusCodes.filter(
        code => !RESOLVED_STATUS_CODES.includes(code),
      )
    }
    statusList = filterStatusList(statusList, validPassedStatusCodes)

    const activeStatusIndex = statusList.findIndex(
      stepStatus =>
        stepStatus.code ===
        validPassedStatusCodes[validPassedStatusCodes.length - 1],
    )

    //DESCOMENTAR CUANDO SE ACTIVE DEVUELTOS
    const canBeReturnedButton =
      canBeReturned(shipping.info) && !hasIncident && false
        ? {
            labelKey: 'action:return-shipping',
            onClick: () => setActivateReturnShippingInfo(shipping.info),
          }
        : null

    const stepsData = statusList.map((stepData, i) => {
      const isPast = i < activeStatusIndex

      const getButton = () => {
        if (i === activeStatusIndex) {
          if (
            getIsIncidentResolutionBtnShown(
              shipping.info.incident.code,
              statusList[i]?.code,
            )
          ) {
            return {
              labelKey: 'noun:resolve-incident',
              onClick: () =>
                onClickResolveIncident({
                  shippingCode: shipping.info.shippingCode,
                  incidentTypeCode: shipping.info.incident.code,
                }),
            }
          } else if (
            getIsDownloadPODAllowed(stepData.code) &&
            !['CIEX', 'CIES', 'CIEL'].includes(
              shipping.info.bestShippingTypeCode,
            )
          ) {
            return {
              labelKey: 'action:download-pod',
              onClick: () => downloadPOD(shipping.info),
            }
          } else if (canBeReturned(shipping.info) && !hasIncident) {
            return {
              labelKey: 'action:return-shipping',
              onClick: () => setActivateReturnShippingInfo(shipping.info),
            }
          }
        }

        if (
          stepData.code === SHIPPING_STATUS_CODES.collected &&
          getIsUnscheduledPickup(shipping, stepData.code, currentStatusCode)
        ) {
          return {
            labelKey: 'action:schedule-pickup',
            onClick: () => setScheduledPickupShippingsInfo([shipping.info]),
          }
        }
      }

      return {
        code: stepData.code,
        label:
          i === 0 && isCancelled
            ? formatMessage('tracking-line:shipping-cancelled')
            : stepData.name,
        description:
          i === activeStatusIndex
            ? capitalize(lastStatus.incidentDescription)
            : null,
        type: getStatusType(
          i,
          stepData,
          isPast,
          isCancelled,
          activeStatusIndex,
          validPassedStatusCodes,
          hasIncident,
          currentStatusCode,
          shipping.info.pickupUnscheduled,
        ),
        nextStepType:
          i !== statusList.length - 1
            ? getStatusType(
                i + 1,
                statusList[i + 1],
                false,
                isCancelled,
                activeStatusIndex,
                validPassedStatusCodes,
                hasIncident,
                currentStatusCode,
              )
            : null,
        button: getButton(),
        isResolved: i === activeStatusIndex && isCurrentStatusResolved,
      }
    })
    const isDelivered = currentStatusCode === SHIPPING_STATUS_CODES.delivered
    const hasReturnCode =
      !!shipping.info.relatedReturn?.reverseReturnShippingCode

    if (isDelivered && hasReturnCode) {
      const returnedStatus = {
        code: 'WEB2500',
        label: formatMessage('tracking:returned'),
        description: null,
        type: CURRENT,
        nextStepType: null,
        button: null,
        isResolved: false,
      }

      stepsData.push(returnedStatus)
      if (stepsData.length > 1) {
        stepsData[stepsData.length - 2].type = PAST
      }
    }

    if (
      !stepsData.some(step => step.type === 'current' || step.type === 'error')
    ) {
      let index = stepsData.findIndex(
        item => item.code === SHIPPING_STATUS_CODES.manifested,
      )

      stepsData[index].type = 'current'
    }

    return {
      stepsData,
      activeStatusIndex,
      currentStatusCode,
      canBeReturnedButton,
    }
  }

  return {
    getTrackingData,
    activateReturnShippingInfo,
    cancelActivateReturn,
    scheduledPickupShippingsInfo,
    cancelScheduledPickup,
  }
}

export default useTrackingLine
